import { ThemeProvider } from 'next-themes'
import * as React from 'react'
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom'

import { AppShell } from '@/components/app-shell'
import { CoreGate, type CoreDomain } from '@/components/core-gate'
import { RouteBoundary } from '@/components/route-boundary'
import { Empty, Loading } from '@/components/states'
import { lazyPage, type LazyPage } from '@/lib/lazy-page'
import { SessionProvider } from '@/lib/session'
import { RealtimeProvider } from './lib/realtime'
import { CoreProvider, useCore } from '@/modules/projects/store'
import { AvatarUrlProvider } from '@/lib/avatars'
import { NotificationsProvider } from '@/modules/notifications/use-notifications'

/*
 * Todas as rotas são sob demanda (Onda 6.2): o JS inicial leva só o shell, os
 * providers e o store. Cada página é um chunk, baixado quando a rota abre — no
 * celular, quem só usa Notas nunca baixa o Budget, o DevOps ou o Excalidraw.
 */
const DashboardPage = lazyPage(() => import('@/modules/core/DashboardPage'), 'DashboardPage')
const NotificationsPage = lazyPage(() => import('@/modules/notifications/NotificationsPage'), 'NotificationsPage')
const ProjectsPage = lazyPage(() => import('@/modules/projects/ProjectsPage'), 'ProjectsPage')
const ProjectBoardPage = lazyPage(() => import('@/modules/projects/ProjectBoardPage'), 'ProjectBoardPage')
const MyActivitiesPage = lazyPage(() => import('@/modules/projects/MyActivitiesPage'), 'MyActivitiesPage')
const TeamLoadPage = lazyPage(() => import('@/modules/projects/TeamLoadPage'), 'TeamLoadPage')
const ProcessesPage = lazyPage(() => import('@/modules/projects/ProcessesPage'), 'ProcessesPage')
const CadernoPage = lazyPage(() => import('@/modules/caderno/CadernoPage'), 'default')
const NotePage = lazyPage(() => import('@/modules/caderno/NotePage'), 'default')
const TicketsPage = lazyPage(() => import('@/modules/tickets/TicketsPage'), 'TicketsPage')
const PeoplePage = lazyPage(() => import('@/modules/people/PeoplePage'), 'PeoplePage')
const OrgChartPage = lazyPage(() => import('@/modules/people/OrgChartPage'), 'OrgChartPage')
const RoutinesPage = lazyPage(() => import('@/modules/routines/RoutinesPage'), 'RoutinesPage')
const AgendaPage = lazyPage(() => import('@/modules/agenda/AgendaPage'), 'AgendaPage')
const HolidaysPage = lazyPage(() => import('@/modules/agenda/HolidaysPage'), 'HolidaysPage')
const IndicatorsPage = lazyPage(() => import('@/modules/indicators/IndicatorsPage'), 'IndicatorsPage')
const GoldPage = lazyPage(() => import('@/modules/indicators/GoldPage'), 'GoldPage')
const DevOpsPage = lazyPage(() => import('@/modules/devops/DevOpsPage'), 'DevOpsPage')
const MonitoringPage = lazyPage(() => import('@/modules/monitoring/MonitoringPage'), 'MonitoringPage')
const SystemsPage = lazyPage(() => import('@/modules/systems/SystemsPage'), 'SystemsPage')
const BudgetPage = lazyPage(() => import('@/modules/finance/budget/BudgetPage'), 'default')
const ExpensesPage = lazyPage(() => import('@/modules/finance/FinancePages'), 'ExpensesPage')
const FinanceReportsPage = lazyPage(() => import('@/modules/finance/reports/ReportsPage'), 'FinanceReportsPage')
const IntegrationsPage = lazyPage(() => import('@/modules/admin/AdminPages'), 'IntegrationsPage')
const AuditPage = lazyPage(() => import('@/modules/admin/AdminPages'), 'AuditPage')

/**
 * Uma rota: o `CoreGate` mostra carregando/erro das cargas do store antes de a
 * página decidir "vazio", e o chunk da página já vem baixando em paralelo.
 * Rota nova sem `domains` volta a mostrar vazio falso — não esquecer.
 */
function Page({ page: Component, domains }: { page: LazyPage; domains?: readonly CoreDomain[] }) {
  const { pathname } = useLocation()
  React.useEffect(() => {
    void Component.preload()
  }, [Component])

  const content = (
    <RouteBoundary resetKey={pathname}>
      <React.Suspense fallback={<Loading />}>
        <Component />
      </React.Suspense>
    </RouteBoundary>
  )
  return domains ? <CoreGate domains={domains}>{content}</CoreGate> : content
}

/** Assina em lote as fotos de todo o time carregado (ver lib/avatars.tsx). */
function TeamAvatars({ children }: { children: React.ReactNode }) {
  const { state } = useCore()
  const paths = React.useMemo(
    () => state.members.flatMap((m) => (m.avatarPath ? [m.avatarPath] : [])),
    [state.members],
  )
  return <AvatarUrlProvider paths={paths}>{children}</AvatarUrlProvider>
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <SessionProvider>
        <RealtimeProvider>
        <CoreProvider>
          <TeamAvatars>
          <NotificationsProvider>
          <Router>
            <AppShell>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Page page={DashboardPage} domains={['projects', 'people', 'core']} />} />

                <Route path="/notificacoes" element={<Page page={NotificationsPage} domains={['people']} />} />

                {/* C2 — Projetos e kanban */}
                <Route path="/projetos" element={<Page page={ProjectsPage} domains={['projects', 'people']} />} />
                <Route path="/projetos/:projectId" element={<Page page={ProjectBoardPage} domains={['projects', 'people']} />} />
                <Route path="/minhas-atividades" element={<Page page={MyActivitiesPage} domains={['projects', 'people']} />} />
                <Route path="/carga-do-time" element={<Page page={TeamLoadPage} domains={['projects', 'people']} />} />
                <Route path="/processos" element={<Page page={ProcessesPage} domains={['projects']} />} />

                {/* C11 — Caderno. people/projects só alimentam menções e vínculos. */}
                <Route path="/caderno" element={<Page page={CadernoPage} domains={['people', 'projects']} />} />
                <Route path="/caderno/:noteId" element={<Page page={NotePage} domains={['people', 'projects']} />} />

                {/* C4 — Chamados */}
                <Route path="/chamados" element={<Page page={TicketsPage} domains={['core', 'projects', 'people']} />} />

                {/* C1 — Pessoas */}
                <Route path="/time" element={<Page page={PeoplePage} domains={['people']} />} />
                <Route path="/organograma" element={<Page page={OrgChartPage} domains={['people']} />} />

                {/* C5 — Rotinas e agenda */}
                <Route path="/rotinas" element={<Page page={RoutinesPage} domains={['core', 'people']} />} />
                <Route path="/agenda" element={<Page page={AgendaPage} domains={['core', 'projects']} />} />
                <Route path="/agenda/feriados" element={<Page page={HolidaysPage} />} />

                {/* C8 e C9 */}
                <Route path="/indicadores" element={<Page page={IndicatorsPage} domains={['core', 'projects']} />} />
                <Route path="/gold" element={<Page page={GoldPage} domains={['core', 'people']} />} />

                {/* C6 e C7 */}
                <Route path="/devops" element={<Page page={DevOpsPage} domains={['core', 'people']} />} />
                <Route path="/monitoramento" element={<Page page={MonitoringPage} domains={['core', 'projects', 'people']} />} />
                <Route path="/sistemas" element={<Page page={SystemsPage} domains={['projects', 'people']} />} />

                {/* C10 — Financeiro */}
                <Route path="/financeiro/budget" element={<Page page={BudgetPage} domains={['core']} />} />
                {/* Endereço antigo (links salvos, SYS): a tela é uma só. */}
                <Route path="/financeiro/budgets" element={<Navigate to="/financeiro/budget" replace />} />
                <Route path="/financeiro/lancamentos" element={<Page page={ExpensesPage} domains={['core']} />} />
                <Route path="/financeiro/relatorios" element={<Page page={FinanceReportsPage} domains={['core']} />} />

                {/* Transversais */}
                <Route path="/admin/integracoes" element={<Page page={IntegrationsPage} domains={['core']} />} />
                <Route path="/admin/auditoria" element={<Page page={AuditPage} domains={['core', 'people']} />} />

                <Route
                  path="*"
                  element={
                    <Empty
                      title="Rota não encontrada"
                      description="O endereço acessado não existe no Hub."
                    />
                  }
                />
              </Routes>
            </AppShell>
          </Router>
          </NotificationsProvider>
          </TeamAvatars>
        </CoreProvider>
        </RealtimeProvider>
      </SessionProvider>
    </ThemeProvider>
  )
}
